import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../../../../components/ui/Dialog";
import Input from "../../../../components/ui/Input";
import Button from "../../../../components/ui/Button";
import Select from "../../../../components/ui/Select";
import { Checkbox } from "../../../../components/ui/Checkbox";
import AppIcon from "../../../../components/AppIcon";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CompanyGrossPayInput, CompanyGrossPayView, AmountCalculationType, GrossPayType, Taxability } from "../../../../models/payroll";
import { useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (input: CompanyGrossPayInput) => void;
  input: CompanyGrossPayInput;
  fixedSalaryComponents: CompanyGrossPayView[];
};

const GrossPaySettingDialog: React.FC<Props> = ({ open, onClose, onSave, input, fixedSalaryComponents }) => {
  const { t } = useTranslation(['payroll','common']);
  const [localInput, setLocalInput] = useState<CompanyGrossPayInput>(input);
  const [hideFormula, setHideFormula] = useState(false);
  const [hideAmount, setHideAmount] = useState(false);

  useEffect(() => {
    setLocalInput(input);
    switch (input.amountCalculationType) {
      case AmountCalculationType.EmployeeSpecificAmount:
        setHideFormula(true);
        setHideAmount(true);
        break;
      case AmountCalculationType.FormulaBasedAmount:
        setHideFormula(false);
        setHideAmount(true);
        break;
      case AmountCalculationType.FixedAmount:
        setHideFormula(true);
        setHideAmount(false);
        break;
      default:
        break;
    }
  }, [input]);

  const handleSave = () => {
    onSave(localInput);
  };

  const amountCalcOptions = Object.values(AmountCalculationType).map((x) => ({
    value: x,
    label: t(`payroll:${x.toString()}`),
  }));

  const grossPayTypeOptions = Object.values(GrossPayType).map((x) => ({
    value: x.valueOf().toString(),
    label: t(`payroll:${x.toString()}`),
  }));

  const taxabilityOptions = Object.values(Taxability).map((x) => ({
    value: x.valueOf().toString(),
    label: t(`payroll:${x.toString()}`),
  }));

  const formulaBaseOptions = fixedSalaryComponents.map((x) => ({
    value: x.id.toString(),
    label: x.name,
  }));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent size="sm" data-testid="settings-form-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-primary/10 p-1.5">
              <AppIcon name="Settings" size={18} className="text-primary" />
            </div>
            <DialogTitle>
              {input.id ? t('payroll:Edit Gross Pay') : t('payroll:Add New Gross Pay')}
            </DialogTitle>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              id="name"
              label={t('payroll:Gross Pay Name')}
              type="text"
              value={localInput.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, name: e.target.value })}
            />
            <Select
              label={t('payroll:Amount Calculation Type')}
              searchable
              options={amountCalcOptions}
              value={localInput.amountCalculationType}
              onChange={(val: string) => {
                const newInput = { ...localInput };
                switch (val) {
                  case AmountCalculationType.EmployeeSpecificAmount:
                    newInput.amountCalculationType = AmountCalculationType.EmployeeSpecificAmount; setHideFormula(true); setHideAmount(true); break;
                  case AmountCalculationType.FormulaBasedAmount:
                    newInput.amountCalculationType = AmountCalculationType.FormulaBasedAmount; setHideFormula(false); setHideAmount(true); break;
                  case AmountCalculationType.FixedAmount:
                    newInput.amountCalculationType = AmountCalculationType.FixedAmount; setHideFormula(true); setHideAmount(false); break;
                }
                setLocalInput(newInput);
              }}
            />
            {hideFormula && hideAmount && (
              <Checkbox
                checked={localInput.isPrimary}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, isPrimary: e.target.checked })}
                label={t('payroll:Base Salary Gross Pay')}
              />
            )}
            <Checkbox
              checked={localInput.includedInWps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, includedInWps: e.target.checked })}
              label={t('common:includedInWps')}
            />
            <hr className="border-border" />
            <Select
              label={t('payroll:Gross Pay Type')}
              searchable
              options={grossPayTypeOptions}
              value={localInput.type.toString()}
              onChange={(val: string) => setLocalInput({ ...localInput, type: (val as GrossPayType) || GrossPayType.Allowance })}
            />
            <Select
              label={t('payroll:Taxability')}
              searchable
              options={taxabilityOptions}
              value={localInput.taxability.toString()}
              onChange={(val: string) => setLocalInput({ ...localInput, taxability: (val as Taxability) || Taxability.Exempted })}
            />
            <fieldset>
              <legend className="text-sm font-medium text-foreground mb-2">{t('payroll:Proof Required')}</legend>
              <RadioGroupPrimitive.Root
                value={localInput.proofRequired ? 'YES' : 'NO'}
                onValueChange={(value) => setLocalInput({ ...localInput, proofRequired: value === 'YES' })}
                className="flex items-center gap-4 rtl:space-x-reverse"
              >
                {[
                  { value: 'YES', label: t('common:Yes') },
                  { value: 'NO', label: t('common:No') },
                ].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupPrimitive.Item
                      value={opt.value}
                      className="h-4 w-4 rounded-full border border-primary text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=checked]:border-primary"
                    >
                      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
                        <span className="h-2 w-2 rounded-full bg-primary" />
                      </RadioGroupPrimitive.Indicator>
                    </RadioGroupPrimitive.Item>
                    <span className="text-sm text-foreground">{opt.label}</span>
                  </label>
                ))}
              </RadioGroupPrimitive.Root>
            </fieldset>
            {!hideAmount && (
              <Input
                id="amount"
                label={t('payroll:Amount')}
                type="number"
                value={localInput.amount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalInput({ ...localInput, amount: parseFloat(e.target.value) })}
                min={0}
              />
            )}
            {!hideFormula && (
              <div className="flex items-center gap-3 flex-grow">
                <div className="flex-[0_0_40%]">
                  <div className="space-y-2">
                    <label htmlFor="formulaPercentage" className="text-sm font-medium text-foreground">
                      {t('payroll:Formula Percentage')}
                    </label>
                    <div className="relative">
                      <input
                        id="formulaPercentage"
                        type="number"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pe-8 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        value={localInput.formulaPercentage}
                        onChange={(e) => setLocalInput({ ...localInput, formulaPercentage: parseFloat(e.target.value) })}
                      />
                      <span className="absolute end-3 top-1/2 -translate-y-1/2 font-semibold text-primary">%</span>
                    </div>
                  </div>
                </div>
                <div className="flex-[1_1_60%]">
                  <Select
                    label={t('payroll:Gross Pays')}
                    searchable
                    options={formulaBaseOptions}
                    value={localInput.companyGrossPayForFormulaId ? localInput.companyGrossPayForFormulaId.toString() : ''}
                    onChange={(val: string) => setLocalInput({ ...localInput, companyGrossPayForFormulaId: val ? parseInt(val) : 0 })}
                  />
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common:Cancel')}
          </Button>
          <Button onClick={handleSave} data-testid="save-btn">
            {t('common:Submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GrossPaySettingDialog;
